import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useCreateDiscountCode } from '../../api/discountAdmin';
import './CreateDiscountModal.css';

interface CreateDiscountModalProps {
  onClose: () => void;
}

const CreateDiscountModal: React.FC<CreateDiscountModalProps> = ({ onClose }) => {
  const [code, setCode] = useState('');
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [campId, setCampId] = useState('');
  const [isSingleUse, setIsSingleUse] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createDiscountCode = useCreateDiscountCode();

  const handleSave = async () => {
    if (!code.trim()) {
      setError('Code is required.');
      return;
    }
    const value = parseFloat(discountValue);
    if (!discountValue || isNaN(value) || value <= 0) {
      setError('Discount value must be a positive number.');
      return;
    }
    if (discountType === 'percentage' && value > 100) {
      setError('Percentage discount cannot exceed 100.');
      return;
    }

    setError(null);
    try {
      await createDiscountCode.mutateAsync({
        code: code.trim().toUpperCase(),
        camp_id: campId ? parseInt(campId, 10) : null,
        discount_type: discountType,
        discount_value: value,
        is_single_use: isSingleUse,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create discount code.');
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="create-discount-modal">
        <button className="modal-close-btn" onClick={onClose} disabled={createDiscountCode.isPending}>
          <X />
        </button>

        <h2 className="create-discount-modal__title">New Discount Code</h2>

        {error && <div className="message message--error">{error}</div>}

        <div className="create-discount-modal__form">
          <div className="form-group">
            <label htmlFor="discount-code">Code</label>
            <input
              id="discount-code"
              type="text"
              className="form-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={createDiscountCode.isPending}
              placeholder="e.g. SUMMER20"
              autoComplete="off"
            />
          </div>

          <div className="create-discount-modal__row">
            <div className="form-group">
              <label htmlFor="discount-type">Type</label>
              <select
                id="discount-type"
                className="form-input"
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value as 'percentage' | 'fixed')}
                disabled={createDiscountCode.isPending}
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed ($)</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="discount-value">
                Value {discountType === 'percentage' ? '(%)' : '($)'}
              </label>
              <input
                id="discount-value"
                type="number"
                className="form-input"
                value={discountValue}
                min="0.01"
                step="0.01"
                max={discountType === 'percentage' ? 100 : undefined}
                onChange={(e) => setDiscountValue(e.target.value)}
                disabled={createDiscountCode.isPending}
                placeholder={discountType === 'percentage' ? '10' : '25.00'}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="camp-id">Camp ID (optional)</label>
            <input
              id="camp-id"
              type="number"
              className="form-input"
              value={campId}
              min="1"
              onChange={(e) => setCampId(e.target.value)}
              disabled={createDiscountCode.isPending}
              placeholder="Leave blank for global"
            />
          </div>

          <div className="create-discount-modal__checkbox-row">
            <input
              id="single-use"
              type="checkbox"
              checked={isSingleUse}
              onChange={(e) => setIsSingleUse(e.target.checked)}
              disabled={createDiscountCode.isPending}
            />
            <label htmlFor="single-use">Single use only</label>
          </div>
        </div>

        <div className="create-discount-modal__actions">
          <button className="btn btn--secondary btn--md" onClick={onClose} disabled={createDiscountCode.isPending}>
            Cancel
          </button>
          <button className="btn btn--primary btn--md" onClick={handleSave} disabled={createDiscountCode.isPending}>
            {createDiscountCode.isPending ? 'Creating...' : 'Create Code'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateDiscountModal;
